import React, { useCallback, useEffect, useState } from 'react';
import { SJsonViewerContainer } from './JsonViewer.styles';
import { Button } from 'antd';

interface JsonViewerProps {
  data: any;
  maxHeight?: string;
  showCopyButton?: boolean;
  expanded?: boolean;
  initiallyCollapsed?: boolean;
  textColor?: string;
}

export const JsonViewer: React.FC<JsonViewerProps> = ({ 
  data, 
  maxHeight, 
  showCopyButton = true, 
  initiallyCollapsed = false, 
  expanded = true,
  textColor
}) => {
  const [isOpen, setIsOpen] = useState<Record<string, boolean>>({});
  const [codeCopied, setCodeCopied] = useState<boolean>(false);
  const [collapsed, setCollapsed] = useState<boolean>(false);

  const generateOpenKeys = useCallback((data: any, depth: number = 1, parentKey: string[] = []): Record<string, boolean> => {
    let keys: Record<string, boolean> = {};

    const parseIfNeeded = (value: any) => {
      if (typeof value === 'string') {
        try {
          return JSON.parse(value);
        } catch {
          return value;
        }
      }
      return value;
    };

    const traverse = (obj: any, currentDepth: number, currentPath: string[]) => {
      if (typeof obj !== 'object' || obj === null) return;

      Object.keys(obj).forEach((key) => {
        const value = parseIfNeeded(obj[key]);
        const newPath = [...currentPath, key];
        const newKey = `${currentDepth}-${newPath.join('-')}`;

        keys[newKey] = true;

        if (typeof value === 'object' && value !== null) {
          traverse(value, currentDepth + 1, newPath);
        }
      });
    };

    traverse(parseIfNeeded(data), depth, parentKey);
    return keys;
  }, []);

  useEffect(() => {
    setCollapsed(initiallyCollapsed);
  }, [initiallyCollapsed]);

  useEffect(() => {
    setIsOpen(expanded ? generateOpenKeys(data) : {});
  }, [data, expanded, generateOpenKeys]);

  useEffect(() => {
    const timer = setTimeout(() => setCodeCopied(false), 3000);
    return () => clearTimeout(timer);
  }, [codeCopied]);

  const toggleVisibility = useCallback((key: string) => {
    setIsOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const copyToClipboard = useCallback((jsonString: string) => {
    navigator.clipboard
      .writeText(jsonString)
      .then(() => setCodeCopied(true))
      .catch((err) => console.error('Error copying json:', err));
  }, []);

  const parseJsonIfPossible = (inputString: string) => {
    try {
      const jsonObject = JSON.parse(inputString);
      return jsonObject;
    } catch (error) {
      return inputString;
    }
  };

  const formatValue = (value: any): string => {
    if (typeof value === 'string') {
      return value.replace(/^"|"$/g, '').replace(/\\"/g, '"').replace(/\\\\/g, '\\');
    }
    return String(value);
  };

  const renderObject = useCallback(
    (obj: any, depth: number = 1, parentKey: string = ''): JSX.Element => {
      const entries = Object.entries(obj);

      return (
        <div>
          {'{'}
          {entries.length > 0 && (
            <>
              <br />
              {entries.map(([key, value], index) => {
                const parsedValue = typeof value === 'string' ? parseJsonIfPossible(value) : value;
                const isLastEntry = index === entries.length - 1;
                const newPath = parentKey ? `${parentKey}-${key}` : key;
                const newKey = `${depth}-${newPath}`;
                const isObject = typeof parsedValue === 'object' && parsedValue !== null;
                const isVisible = isOpen[newKey];

                return (
                  <div key={newKey} className="property">
                    {isObject && (
                      <span className="toggle-symbol" onClick={() => toggleVisibility(newKey)}>
                        {isVisible ? '▾' : '▸'}
                      </span>
                    )}
                    <span className="property-name">{key}: </span>
                    {isObject ? (
                      isVisible ? (
                        <div style={{ marginLeft: '20px' }}>
                          {renderObject(parsedValue, depth + 1, newPath)}
                        </div>
                      ) : (
                        '{...}'
                      )
                    ) : (
                      <span className="value">{formatValue(parsedValue)}</span>
                    )}
                    {!isLastEntry && ','}
                  </div>
                );
              })}
            </>
          )}
          {'}'}
        </div>
      );
    },
    [isOpen, toggleVisibility]
  );

  const containerStyle = textColor ? { color: textColor } : {};

  return (
    <SJsonViewerContainer style={containerStyle}>
      <div className="json-wrapper">
        {initiallyCollapsed && (
          <>
            <span className="root-toggle-symbol" onClick={() => setCollapsed(!collapsed)}>
              {collapsed ? '▸' : '▾'}
            </span>
            {collapsed && '{...}'}
          </>
        )}
        {!collapsed && (
          <div style={{ maxHeight: maxHeight || 'auto' }} className="json-viewer">
            {typeof data === 'object' && data !== null ? (
              renderObject(data)
            ) : (
              <span className="value">{formatValue(data)}</span>
            )}
          </div>
        )}
      </div>
      {showCopyButton && (
        <Button className="copy-button" onClick={() => copyToClipboard(JSON.stringify(data, null, 2))}>
          {codeCopied ? 'Copied...' : 'Copy JSON'}
        </Button>
      )}
    </SJsonViewerContainer>
  );
};
